import { shouldOpenCogginsPdfInNewTab } from './cogginsPdfViewer.ts';

export async function fetchHorseById(horseId: string) {
  const token = localStorage.getItem('authToken');
  const response = await fetch(`/api/horses/${horseId}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });
  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.message || 'Failed to load horse');
  }
  return data.data;
}

export async function uploadHorseImage(horseId: string, file: File) {
  const token = localStorage.getItem('authToken');
  const formData = new FormData();
  formData.append('horseImage', file);

  const response = await fetch(`/api/horses/${horseId}/upload-image`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`
    },
    body: formData
  });

  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.message || 'Failed to upload horse photo');
  }
  return data;
}

export async function uploadHorseCogginsDocument(horseId: string, file: File) {
  const token = localStorage.getItem('authToken');
  const formData = new FormData();
  formData.append('cogginsDocument', file);

  const response = await fetch(`/api/horses/${horseId}/upload-coggins`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`
    },
    body: formData
  });

  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.message || 'Failed to upload Coggins document');
  }
  return data;
}

export async function deleteHorseCogginsDocument(horseId: string) {
  const token = localStorage.getItem('authToken');
  const response = await fetch(`/api/horses/${horseId}/coggins-document`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });

  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.message || 'Failed to remove Coggins document');
  }
  return data;
}

export const COGGINS_ACCEPT =
  'application/pdf,image/jpeg,image/png,image/gif,image/heic,image/heif,image/webp,.pdf,.heic,.heif';

export type CogginsViewerPayload = {
  blobUrl: string;
  contentType: string;
  fileName: string;
};

export const COGGINS_VIEW_EVENT = 'ggaa:coggins-view';

/** Same-origin view URL so mobile can open the file in one tap (no blob iframe). */
export function cogginsDocumentViewUrl(horseId: string, authToken: string | null): string {
  const params = new URLSearchParams();
  if (authToken) params.set('accessToken', authToken);
  const qs = params.toString();
  return `/api/horses/${encodeURIComponent(horseId)}/coggins-document/view${qs ? `?${qs}` : ''}`;
}

function openCogginsInBrowserTab(horseId: string, authToken: string | null): void {
  const url = cogginsDocumentViewUrl(horseId, authToken);
  const opened = window.open(url, '_blank', 'noopener,noreferrer');
  if (!opened) {
    window.location.assign(url);
  }
}

function parseFilenameFromContentDisposition(header: string | null): string | null {
  if (!header) return null;
  const utf8 = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8?.[1]) {
    try {
      return decodeURIComponent(utf8[1]);
    } catch {
      return utf8[1];
    }
  }
  const plain = header.match(/filename="([^"]+)"/i);
  return plain?.[1] || null;
}

function inferCogginsMimeType(headerType: string | null, fileName: string): string {
  const normalized = (headerType || '').split(';')[0].trim().toLowerCase();
  if (normalized && normalized !== 'application/octet-stream') {
    return normalized;
  }
  const ext = fileName.includes('.') ? fileName.split('.').pop()?.toLowerCase() : '';
  const byExt: Record<string, string> = {
    pdf: 'application/pdf',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    webp: 'image/webp',
    heic: 'image/heic',
    heif: 'image/heif'
  };
  return (ext && byExt[ext]) || normalized || 'application/octet-stream';
}

function dispatchCogginsView(detail: CogginsViewerPayload): void {
  window.dispatchEvent(new CustomEvent<CogginsViewerPayload>(COGGINS_VIEW_EVENT, { detail }));
}

/** Desktop: in-app viewer. Phone/PWA: open the authenticated view URL in the browser (one tap). */
export async function openHorseCogginsDocument(horseId: string): Promise<void> {
  const token = localStorage.getItem('authToken');

  if (shouldOpenCogginsPdfInNewTab()) {
    if (!token) {
      window.alert('Please sign in again to view this document.');
      return;
    }
    openCogginsInBrowserTab(horseId, token);
    return;
  }

  const response = await fetch(`/api/horses/${horseId}/coggins-document/view`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });

  if (!response.ok) {
    let message = 'Could not open Coggins document.';
    try {
      const data = await response.json();
      if (data.message) message = data.message;
    } catch {
      /* not JSON */
    }
    window.alert(message);
    return;
  }

  const fileName =
    parseFilenameFromContentDisposition(response.headers.get('Content-Disposition')) ||
    'Coggins-document';
  let contentType = inferCogginsMimeType(response.headers.get('Content-Type'), fileName);
  if (/\.pdf$/i.test(fileName) && !contentType.includes('pdf')) {
    contentType = 'application/pdf';
  }
  const blob = await response.blob();
  const typedBlob =
    blob.type && blob.type !== 'application/octet-stream'
      ? blob
      : new Blob([await blob.arrayBuffer()], { type: contentType });
  const blobUrl = URL.createObjectURL(typedBlob);

  dispatchCogginsView({ blobUrl, contentType, fileName });
}
